import { AiChatChart, AiChatFile, AiChatMessage } from 'src/services/aiChat';

/** A chat message plus any files or charts the assistant attached to it. */
export type ChatEntry = AiChatMessage & { files?: AiChatFile[]; charts?: AiChatChart[]; feedback?: 'up' | 'down' };

export interface AiChatConversation {
  id: string;
  title: string;
  updatedAt: string;
  messages: ChatEntry[];
}

export interface ConversationGroup<T extends { id: string; updatedAt: string } = AiChatConversation> {
  /** 'today' | 'yesterday' | ISO date (yyyy-mm-dd) for older days. */
  key: string;
  items: T[];
}

const STORAGE_KEY = 'ai_chat_conversations';
const MAX_CONVERSATIONS = 30;
const TITLE_MAX_LENGTH = 60;

export const createConversationId = (): string => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export const conversationTitle = (messages: ChatEntry[]): string => {
  const firstUserMessage = messages.find(m => m.role === 'user');
  const source = firstUserMessage?.content.trim() || firstUserMessage?.attachments?.[0]?.filename || '';
  const collapsed = source.replace(/\s+/g, ' ').trim();
  return collapsed.length > TITLE_MAX_LENGTH ? `${collapsed.slice(0, TITLE_MAX_LENGTH - 1)}…` : collapsed;
};

export const conversationHasFiles = (conversation: AiChatConversation): boolean =>
  conversation.messages.some(m => (m.files?.length ?? 0) > 0);

export function loadConversations(): AiChatConversation[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as AiChatConversation[];
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(c => c && typeof c.id === 'string' && Array.isArray(c.messages))
      .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt));
  } catch {
    return [];
  }
}

export function upsertConversation(conversation: AiChatConversation): AiChatConversation[] {
  const others = loadConversations().filter(c => c.id !== conversation.id);
  const next = [conversation, ...others]
    .sort((a, b) => b.updatedAt.localeCompare(a.updatedAt))
    .slice(0, MAX_CONVERSATIONS);
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  } catch {
    // Storage full or unavailable — the chat itself keeps working without history.
  }
  return next;
}

/**
 * Drops the whole conversation history.
 *
 * Called from `resetClientState()` on logout: these are up to 30 full
 * conversations including every message body, i.e. user content, not a device
 * preference. On a shared workstation the next user must not be able to read
 * them.
 */
export function clearConversations(): void {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage unavailable — there is nothing persisted then either.
  }
}

const dayKey = (date: Date): string =>
  `${date.getFullYear()}-${`${date.getMonth() + 1}`.padStart(2, '0')}-${`${date.getDate()}`.padStart(2, '0')}`;

/** Groups conversations (already sorted newest-first) into today / yesterday / older calendar days. */
export function groupConversationsByDay<T extends { id: string; updatedAt: string }>(
  conversations: T[],
  now: Date = new Date(),
): ConversationGroup<T>[] {
  const todayKey = dayKey(now);
  const yesterdayKey = dayKey(new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1));

  const groups: ConversationGroup<T>[] = [];
  conversations.forEach(conversation => {
    const key = dayKey(new Date(conversation.updatedAt));
    let groupKey = key;
    if (key === todayKey) groupKey = 'today';
    else if (key === yesterdayKey) groupKey = 'yesterday';
    const group = groups.find(g => g.key === groupKey);
    if (group) group.items.push(conversation);
    else groups.push({ key: groupKey, items: [conversation] });
  });
  return groups;
}
